//
// README - Beta Component
//
// This component is marked "beta" and will go through some iterations
// before landing at a final solution. Please reach out to the Cadence team
// if you intend to use this in the meantime and we'll help you out!
//

import React from 'react';
import classNames from 'classnames';

import {
  RCFavoriteButton,
  RCFavoriteButtonProps,
  RCSkeleton,
  RCEyebrow, RCEyebrowProps,
  RCNudge, RCNudgeProps,
  RCPriceBlock, RCPriceBlockProps,
  RCIconButton,
  RCShopInfo, RCShopInfoProps,
} from '../';
import {
  TimesCircleAltIcon,
} from '../../icons/react';

export interface RCListingCardProps {
  /**
   * `linkElement` is Cadence's way to support CoreLink.
   * Pass in a linkable element and in component's internals will determine how to render the `children` prop passed.
   *
   * Example:
   *
   * ```tsx
   * <RCListingCard
   *  title="Foo"
   *  linkElement={(children) => (
   *    <CoreLink
   *      to="/where/ever/you/want/"
   *      target="_blank"
   *    >
   *      {children}
   *    </CoreLink>
   *  )}
   *  ...
   * />
   * ```
   */
  linkElement: (children: React.ReactNode) => React.ReactElement;

  /**
   * Supports <ImageCarousel> only for now. Passing in anything else will likely "work" but may not look/function correctly.
   */
  thumbnailElement: React.ReactElement;

  /** The title of the listing */
  title: string;

  /** The condition of the listing. E.g. `Condition: Brand New`. This component is not responsible for appending `Condition: ` strings */
  conditionLabel?: string;

  /** Card-level param. If true, the card will take up the full height of its parent.
   * Should be `true` when inside a listing grid where all items in a row should be the same height. */
  fullHeight?: boolean;

  /** The small bit of UI under the thumbnail. Should showcase sale info, bump info, or things like that. */
  eyebrow?: RCEyebrowProps;

  /** A human-readable string that shows the listing price */
  price: RCPriceBlockProps;

  /** Structured interface - See RCFavoriteButton for details */
  favorite?: Omit<RCFavoriteButtonProps, 'size' | 'borderless'>;

  /** A `primary` action should be a `primary` RCButton variant. Actions passed in here should be RCButtons, or CoreLinks using RCButton styling. */
  primaryAction?: React.ReactElement;

  /** a `secondary` action should be a `secondary` RCButton. */
  secondaryAction?: React.ReactElement;

  /** Structured interface - See RCNudge for details */
  nudges?: RCNudgeProps[];

  /** Pass in non-UI-rendering HTML content here to help with SEO. Will be injected as the first DOM item of the card */
  metaElement?: React.ReactElement;

  /** Render a remove button to the right of the title area */
  removeButton?: {
    onClick: () => void;
    label: string;
  },

  /** When present, renders a single line of text (that truncates via ellipsis) under the listing title */
  shopLocation?: string;

  /** When present, renders information about the shop - This instance of the nested RCShopInfo doesn't support avatars so the url is omitted. */
  shopInfo?: Omit<RCShopInfoProps, 'avatarURL'>;
}

{/* eslint-disable react/jsx-props-no-spreading */}

export function RCListingCard({
  linkElement,
  thumbnailElement,
  eyebrow = undefined,
  title,
  conditionLabel = undefined,
  price,
  favorite = undefined,
  primaryAction = undefined,
  secondaryAction = undefined,
  nudges = undefined,
  fullHeight = true,
  metaElement = undefined,
  removeButton = undefined,
  shopLocation = undefined,
  shopInfo = undefined,
}: RCListingCardProps) {
  function LinkedTitle({ children, ...passthroughProps }: { children: React.ReactNode }) {
    if (!linkElement) {
      return (
        <div className="rc-listing-card__title">
          {children}
        </div>
      );
    }

    return React.cloneElement(
      linkElement(children),
      {
        className: 'rc-listing-card__title',
        ...passthroughProps,
      },
    );
  }

  return (
    <div className={classNames(
      'rc-listing-card',
      {
        'rc-listing-card--full-height': fullHeight,
        'rc-listing-card--single-action': (!!primaryAction && !secondaryAction) || (!primaryAction && !!secondaryAction),
      },
    )}>
      {metaElement && (
        <div className="rc-listing-card__meta">
          {metaElement}
        </div>
      )}
      <div className="rc-listing-card__inner">
        <div className="rc-listing-card__thumbnail">
          {thumbnailElement}
        </div>
        {favorite && (
          <div className="rc-listing-card__favorite-button">
            <RCFavoriteButton
              {...favorite}
              borderless
            />
          </div>
        )}
        <div className="rc-listing-card__eyebrow">
          {eyebrow &&
            <RCEyebrow {...eyebrow} />
          }
        </div>
        <div className="rc-listing-card__header">
          <div className="rc-listing-card__titleblock">
            <LinkedTitle>
              {title}
            </LinkedTitle>
            <div className="rc-listing-card__condition">
              {conditionLabel}
            </div>
          </div>
          {!!removeButton?.onClick && (
            <div className="rc-listing-card__title__remove-button">
              <RCIconButton
                svgComponent={() => <TimesCircleAltIcon />}
                label={removeButton.label}
                onClick={removeButton.onClick}
                variant="transparent"
                size="small"
              />
            </div>
          )}
        </div>
        {shopLocation && (
          <div className="rc-listing-card__shop-location">
            {shopLocation}
          </div>
        )}
        <div className="rc-listing-card__price">
          <RCPriceBlock {...price} />
        </div>
        {(primaryAction || secondaryAction) && (
          <div className="rc-listing-card__actions">
            {primaryAction && (
              <div className="rc-listing-card__action rc-listing-card__action--primary">
                {primaryAction}
              </div>
            )}
            {secondaryAction && (
              <div className="rc-listing-card__action rc-listing-card__action--secondary">
                {secondaryAction}
              </div>
            )}
          </div>
        )}
        {shopInfo && (
          <div className="rc-listing-card__shop-info">
            <RCShopInfo {...shopInfo} />
          </div>
        )}
        {!!nudges && (
          <div className="rc-listing-card__nudge-block">
            {nudges.map(nudge => (
              <div
                key={nudge.label}
                className="rc-listing-card__nudge"
              >
                <RCNudge {...nudge} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="rc-listing-card">
      <div className="rc-listing-card__inner">
        <div className="rc-listing-card__thumbnail">
          <RCSkeleton
            shape="square"
            display="block"
          />
        </div>
        <div className="rc-listing-card__eyebrow"/>
        <div className="rc-listing-card__titleblock">
          <RCSkeleton count={2} />
          <div className="rc-listing-card__condition">
            <RCSkeleton width="13rem" />
          </div>
        </div>
        <div className="rc-listing-card__price">
          <RCSkeleton width="10rem" height="3.6rem" display="block" />
        </div>
      </div>
    </div>
  );
}

RCListingCard.Skeleton = Skeleton;
